import type { IdListFilter } from '../contacts/contacts.types';
import type { ServiceFetcher } from '../shared/http.types';
import type {
  BankSearchParams,
  BankSearchResponse,
  OwnBankParams,
  OwnBankResponse,
} from './digitalBank.types';

const BANK_PATH = 'bank';

function joinIdList(filter: IdListFilter | undefined): string | undefined {
  if (filter === undefined) {
    return undefined;
  }
  if (Array.isArray(filter)) {
    return filter.length ? filter.join(',') : undefined;
  }
  return filter.length ? filter : undefined;
}

function buildOwnBankParams(params: OwnBankParams): Record<string, string | number> {
  const out: Record<string, string | number> = {};

  if (params.search_text) {
    out.search_text = params.search_text;
  }
  const idListFields: Array<[keyof OwnBankParams, string]> = [
    ['access_level', 'access_level'],
    ['expertise_level', 'expertise_level'],
    ['languages', 'languages'],
    ['industries', 'industries'],
    ['directions', 'directions'],
    ['competences', 'competences'],
    ['countries', 'countries'],
    ['cities', 'cities'],
  ];
  for (const [field, key] of idListFields) {
    const joined = joinIdList(params[field] as IdListFilter | undefined);
    if (joined !== undefined) {
      out[key] = joined;
    }
  }
  if (params.salable !== undefined) {
    out.salable = String(params.salable);
  }
  if (params.folderId !== undefined) {
    out.folderId = params.folderId;
  }
  if (params.take !== undefined) {
    out.take = params.take;
  }
  if (params.page !== undefined) {
    out.page = params.page;
  }

  return out;
}

/** GET /bank — the caller's own Digital Bank (connections ∪ owned contacts). */
export function getOwnBank(
  fetcher: ServiceFetcher,
  params: OwnBankParams = {},
): Promise<OwnBankResponse> {
  return fetcher({
    method: 'GET',
    path: BANK_PATH,
    params: buildOwnBankParams(params),
  }) as Promise<OwnBankResponse>;
}

function buildBankSearchParams(params: BankSearchParams): Record<string, string | number> {
  const out: Record<string, string | number> = {};

  if (params.search_first_name) {
    out.search_first_name = params.search_first_name;
  }
  if (params.search_last_name) {
    out.search_last_name = params.search_last_name;
  }
  const countries = joinIdList(params.countries);
  if (countries !== undefined) {
    out.countries = countries;
  }
  const cities = joinIdList(params.cities);
  if (cities !== undefined) {
    out.cities = cities;
  }

  return out;
}

/** GET /bank/search — browse registered users + salable contacts. */
export function getBankSearch(
  fetcher: ServiceFetcher,
  params: BankSearchParams = {},
): Promise<BankSearchResponse> {
  return fetcher({
    method: 'GET',
    path: `${BANK_PATH}/search`,
    params: buildBankSearchParams(params),
  }) as Promise<BankSearchResponse>;
}
